// framework/client/components/UserProfileMenu.js
import { ComponentBase } from "../core/ComponentBase.js";
import { ThemeSelector } from "./ThemeSelector.js"; // Import the component we will embed
import { LanguageSelector } from "./LanguageSelector.js"; // Import LanguageSelector

export class UserProfileMenu extends ComponentBase {
    static styles = `
        .user-profile-menu-container {
            position: relative;
            display: inline-block;
        }

        .user-avatar {
            width: 40px;
            height: 40px;
            border-radius: var(--ab-radius-full);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: var(--ab-text-md);
            font-weight: var(--ab-weight-bold);
            color: white;
            background-color: var(--bs-info);
            cursor: pointer;
            border: 2px solid var(--bs-border-color-translucent);
            transition: border-color var(--ab-t-base), box-shadow var(--ab-t-base);
        }

        @media (hover: hover) {
            .user-avatar:hover {
                border-color: var(--bs-primary);
            }
        }

        .user-avatar:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }

        .user-avatar img {
            width: 100%;
            height: 100%;
            border-radius: var(--ab-radius-full);
            object-fit: cover;
        }

        /* Remove default vertical padding from the main dropdown menu */
        .dropdown-menu {
            padding-top: 0;
            padding-bottom: 0;
            border-radius: var(--ab-radius-md);
            box-shadow: var(--ab-shadow-lg);
        }

        /* Tighter padding for the user info header */
        .user-info {
            padding: 0.6rem 1rem; /* Reduced from 0.75rem */
            margin-bottom: 0;
        }

        .user-info div:first-child {
            font-weight: var(--ab-weight-medium);
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        /* Tighter padding for each action item */
        .dropdown-item {
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.5rem 1rem; /* Reduced from 0.6rem */
            border-radius: var(--ab-radius-sm);
            color: var(--bs-body-color);
            transition: background-color var(--ab-t-fast), color var(--ab-t-fast);
        }

        @media (hover: hover) {
            .dropdown-item:hover {
                background-color: var(--ab-hover-bg);
                color: var(--bs-body-color);
            }
            .dropdown-item.text-danger:hover {
                color: var(--bs-danger);
                background-color: var(--bs-danger-bg-subtle, rgba(var(--bs-danger-rgb), 0.1));
            }
        }

        .dropdown-item:focus-visible {
            outline: none;
            box-shadow: var(--ab-focus-ring);
        }

        .dropdown-item:active {
            background-color: var(--ab-hover-bg);
        }

        /* Keep icon styles for alignment */
        .dropdown-item i {
            font-size: var(--ab-text-lg);
            width: 1.2rem; 
            text-align: center;
            color: var(--bs-secondary-color);
        }

        .dropdown-item:hover i, .dropdown-item:active i {
            color: inherit;
        }

        /* Tighter padding for the embedded theme/mode switcher */
        .embedded-theme-selector-container {
            padding: 0.1rem 0.25rem; /* Reduced from 0.5rem */
        }

        /* Reduce margin on the <hr> divider to make separators less prominent */
        .dropdown-divider {
            margin: 0.1rem 0; /* Reduced from Bootstrap's default 0.5rem */
        }

        .dropdown-header-row {
            display: none !important;
        }

        @media (max-width: 767px) {
            .dropdown-header-row {
                display: flex !important;
            }
        }
    `;

    constructor(config) {
        super(config);
        this.dropdownMenu = null;
        this.themeSelector = null; // To hold the ThemeSelector component instance
        this.languageSelector = null; // To hold the LanguageSelector component instance
        this._boundProfileUpdateHandler = this.handleProfilePictureUpdate.bind(this);
        this._boundRender = this.render.bind(this); // For event listener cleanup
        this.applyStyles();
    }

    static getDefaultConfig() {
        return {
            profilePicture: null,
            fullName: null,
            username: null,
            email: null,
            showThemeSelector: true, // Set to true by default
            showLanguageSelector: true, // Set to true by default
            showLogout: true,
            hasRemoteDesktop: false,
            logoutText: "Log out",
            menuItems: [],
        };
    }

    async initialize() {
        this.appClient.events.local.on('userProfilePictureUpdated', this._boundProfileUpdateHandler);
        this.appClient.events.local.on('languagesInitialized', this._boundRender); // Re-render when languages are ready
        const userData = this.appClient.getSharedData("user");
        if (userData) this.setUserData(userData);

        if (this.config.showThemeSelector) {
            this.themeSelector = await this.createManagedComponent('ThemeSelector', null, {
                instanceId: `${this.config.instanceId}-theme-selector`,
                disableThemes: true, // Use it as a pure mode switcher
                hideLabel: true,
            });
        }

        await super.initialize();
    }

    async initAfterLogin() {
        const userData = await this.appClient.rpcClient.call('UserProfileRpc.getUserProfile');
        if (userData) {
            this.setUserData(userData);
            if (this.container) await this.render();
        }
    }

    setUserData(userData) {
        if (userData) {
            this.config.username = userData.username || this.config.username;
            this.config.email = userData.email || this.config.email;
            const fullNameFromData = (userData.firstName ? userData.firstName + (userData.lastName ? " " + userData.lastName : "") : null);
            this.config.fullName = fullNameFromData || this.config.fullName;
            this.config.profilePicture = userData.profilePicture || this.config.profilePicture;
        }
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement("div");
            this.container.classList.add("user-profile-menu-container", "dropdown");

            const dropdownToggle = document.createElement("div");
            dropdownToggle.classList.add("user-avatar");
            dropdownToggle.setAttribute("data-bs-toggle", "dropdown");
            dropdownToggle.setAttribute("aria-expanded", "false");
            dropdownToggle.setAttribute("aria-label", "User Profile Menu");
            dropdownToggle.setAttribute("tabindex", "0"); // Add tabindex for keyboard focus
            dropdownToggle.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    dropdownToggle.click();
                }
            });
            this.container.appendChild(dropdownToggle);

            this.dropdownMenu = document.createElement("div");
            this.dropdownMenu.classList.add("dropdown-menu", "dropdown-menu-end");
            this.container.appendChild(this.dropdownMenu);
        }

        const avatar = this.container.querySelector('.user-avatar');
        avatar.setAttribute("title", this.config.fullName || this.config.username || "User Profile Menu");
        avatar.setAttribute("data-skip-text", "true");
        avatar.innerHTML = '';
        if (this.config.profilePicture) {
            const img = document.createElement("img");
            img.src = `/download/${this.config.profilePicture}`;
            img.alt = "User Profile Picture";
            avatar.appendChild(img);
        } else {
            avatar.textContent = this.getInitial();
        }

        await this.renderDropdownContent();
        return this.container;
    }

    getInitial() {
        return (this.config.username || "U").charAt(0).toUpperCase();
    }

    shouldRenderLanguageSelector() {
        const languages = this.appClient.tx.getAvailableLanguages() || [];
        return this.config.showLanguageSelector && languages.length > 1;
    }

    async renderDropdownContent() {
        this.dropdownMenu.innerHTML = ''; // Clear previous content

        // --- Mobile Fullscreen Header ---
        const mobileHeader = document.createElement("div");
        mobileHeader.className = "dropdown-header-row justify-content-between align-items-center border-bottom pb-2 mb-2 w-100";
        mobileHeader.innerHTML = `
            <span class="fw-bold text-body">User Profile Menu</span>
            <button type="button" class="btn-close" data-action="close-dropdown" aria-label="Close"></button>
        `;
        mobileHeader.querySelector('[data-action="close-dropdown"]').addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const dropdownToggle = this.container.querySelector('[data-bs-toggle="dropdown"]');
            if (dropdownToggle) {
                const dropdownInstance = bootstrap.Dropdown.getInstance(dropdownToggle);
                if (dropdownInstance) dropdownInstance.hide();
            }
        });
        this.dropdownMenu.appendChild(mobileHeader);

        // --- Section 1: User Info Header ---
        const userInfo = document.createElement("div");
        userInfo.classList.add("user-info");
        userInfo.innerHTML = `<div>${this.config.fullName || this.config.username || "User"}</div><div class="small text-muted">${this.config.email || "No Email"}</div>`;
        this.dropdownMenu.appendChild(userInfo);

        const hasThemeSettings = this.config.showThemeSelector && this.themeSelector;
        const hasLanguageSettings = this.shouldRenderLanguageSelector();
        const hasSettings = hasThemeSettings || hasLanguageSettings;
        const hasMenuItems = this.config.menuItems && this.config.menuItems.length > 0;
        const hasLogout = this.config.showLogout;
        const hasRemoteDesktop = this.config.showRemoteDesktop;

        // --- Section 2: Theme Selector and Language Selector (if enabled) ---
        if (hasSettings) {
            this.dropdownMenu.appendChild(document.createElement("hr"));

            if (hasThemeSettings) {
                const themeSelectorWrapper = document.createElement('div');
                themeSelectorWrapper.className = 'embedded-theme-selector-container';
                const themeSelectorElement = await this.themeSelector.render();
                themeSelectorWrapper.appendChild(themeSelectorElement);
                this.dropdownMenu.appendChild(themeSelectorWrapper);
            }

            if (hasLanguageSettings) {
                // Create the component just-in-time
                if (!this.languageSelector) {
                    this.languageSelector = await this.createManagedComponent('LanguageSelector', null, {
                        instanceId: `${this.config.instanceId}-language-selector`,
                        renderAs: 'submenuItem',
                    });
                }
                const languageSelectorElement = await this.languageSelector.render();
                this.dropdownMenu.appendChild(languageSelectorElement);
            }
        }

        // --- Section 3: Actions (Profile links, Logout) ---
        if (hasMenuItems || hasLogout) {
            this.dropdownMenu.appendChild(document.createElement("hr"));
        }

        if (hasMenuItems) {
            (this.config.menuItems || []).forEach(item => {
                const menuItem = document.createElement("a");
                menuItem.href = "#";
                menuItem.classList.add("dropdown-item");
                menuItem.innerHTML = `<i class="${item.icon || 'bi bi-person-circle'}"></i><span></span>`;
                const span = menuItem.querySelector('span');
                span.textContent = this.t(item.title);
                span.setAttribute("data-i18n", item.title);

                menuItem.addEventListener('click', (e) => {
                    e.preventDefault();
                    let targetRoute = item.route;
                    if (targetRoute === '/dashboard' || targetRoute === '/' || !targetRoute) {
                        const activeRole = this.appClient.getSharedData('activeRole') || this.appClient.getSharedData('user')?.role;
                        const primaryRole = (Array.isArray(activeRole) ? activeRole[0] : activeRole) || 'user';
                        targetRoute = this.appClient.layoutEngine.defaultRoutes?.[primaryRole] || this.appClient.layoutEngine.defaultRoutes?.user || '/';
                    }
                    this.appClient.layoutEngine.navigate(targetRoute);
                });
                this.dropdownMenu.appendChild(menuItem);
            });
        }

        if (hasRemoteDesktop) {
            const myScreenItem = document.createElement("a");
            myScreenItem.href = "#";
            myScreenItem.classList.add("dropdown-item");
            myScreenItem.innerHTML = `<i class="bi bi-display"></i><span>${this.t('My Screen')}</span>`;
            myScreenItem.addEventListener('click', (e) => {
                e.preventDefault();
                const userId = this.appClient.getSharedData('userId');
                if (userId) {
                    const identityId = `user-${userId}`;
                    this.appClient.events.fireLocal('DISPLAY_REMOTE_SCREEN', { identityId });
                } else {
                    this.appClient.notifier.warn("Could not identify current user to open screen.");
                }
            });
            this.dropdownMenu.appendChild(myScreenItem);
        }

        // App Permissions Action
        const permissionsItem = document.createElement("a");
        permissionsItem.href = "#";
        permissionsItem.classList.add("dropdown-item");
        permissionsItem.innerHTML = `<i class="bi bi-shield-check"></i><span>${this.t('App Permissions')}</span>`;
        permissionsItem.addEventListener('click', (e) => {
            e.preventDefault();
            this.appClient.permissions.promptFirstTimePermissions(true).catch(() => {});
        });
        this.dropdownMenu.appendChild(permissionsItem);

        if (hasLogout) {
            if (hasMenuItems) {
                this.dropdownMenu.appendChild(document.createElement("hr"));
            }

            const logoutButton = document.createElement("button");
            logoutButton.type = 'button';
            logoutButton.classList.add("dropdown-item", "text-danger");
            logoutButton.innerHTML = `<i class="bi bi-box-arrow-right"></i><span></span>`;
            const span = logoutButton.querySelector('span');
            span.textContent = this.t(this.config.logoutText);
            span.setAttribute("data-i18n", this.config.logoutText);

            logoutButton.addEventListener('click', () => this.appClient.sessionTokenHandler.logout());
            this.dropdownMenu.appendChild(logoutButton);
        }

        this.appClient.tx.applyTranslations(this.dropdownMenu);
    }

    handleProfilePictureUpdate({ avatarUrl }) {
        this.config.profilePicture = avatarUrl;
        if (this.container) this.render();
    }

    destroy() {
        this.appClient.events.local.off('userProfilePictureUpdated', this._boundProfileUpdateHandler);
        this.appClient.events.local.off('languagesInitialized', this._boundRender);
        this.themeSelector?.destroy();
        this.languageSelector?.destroy();
        super.destroy();
    }
}